// src/pages/ScoredLeads.jsx — every lead the AI Lead Scoring API has scored.
//
// Reads GET /api/lead-scores?from&to (aiocr, through the same-origin nginx),
// which is one row per call to /api/lead-score or /api/v2/lead-score. The
// contract is in whatsapp-broker/docs/lead_scoring.md. Pick a date range,
// fetch, filter in the table; click a row to see the signals that made the
// score and the form exactly as it was submitted.
import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  createTheme,
  ThemeProvider,
  useMediaQuery,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { Close, Download, Refresh } from '@mui/icons-material';
import * as XLSX from 'xlsx';

const theme = createTheme({
  palette: {
    primary: { main: '#F96168' },
    secondary: { main: '#000000' },
    background: { default: '#ffffff', paper: '#f8fafc' },
  },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', borderRadius: 8, fontWeight: 600 } } },
  },
});

// Colours follow the bands the API documents: HOT / WARM / LOW and
// GENUINE / SUSPICIOUS / SPAM.
const BAND_COLOUR = { HOT: '#16a34a', WARM: '#d97706', LOW: '#64748b' };
const QUALITY_COLOUR = { GENUINE: '#16a34a', SUSPICIOUS: '#d97706', SPAM: '#dc2626' };

function Pill({ value, colours }) {
  const colour = colours[value] || '#64748b';
  return (
    <Chip
      label={value || '-'}
      size="small"
      sx={{ bgcolor: `${colour}1a`, color: colour, fontWeight: 700, borderRadius: 1 }}
    />
  );
}

function ScoreCell({ value, band }) {
  const colour = BAND_COLOUR[band] || '#64748b';
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 90 }}>
      <Typography sx={{ fontWeight: 700, width: 28, textAlign: 'right' }}>{value}</Typography>
      <Box sx={{ flex: 1, height: 6, bgcolor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ width: `${value}%`, height: '100%', bgcolor: colour }} />
      </Box>
    </Stack>
  );
}

const fullName = row => [row.first_name, row.last_name].filter(Boolean).join(' ') || '-';

// One scored lead in full: why it scored what it did, and what was submitted.
function LeadDetail({ id, onClose }) {
  const [row, setRow] = useState(null);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    setRow(null);
    setError('');
    fetch(`/api/lead-scores/${id}`, { cache: 'no-store' })
      .then(async r => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
        return body;
      })
      .then(body => alive && setRow(body))
      .catch(err => alive && setError(err.message));
    return () => { alive = false; };
  }, [id]);

  const result = row?.response || {};
  return (
    <Dialog open={Boolean(id)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{row ? `${fullName(row)} · ${row.business_name || '-'}` : 'Scored lead'}</span>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Typography color="error">{error}</Typography>}
        {!row && !error && <Typography color="text.secondary">Loading…</Typography>}
        {row && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{row.score}</Typography>
              <Pill value={row.band} colours={BAND_COLOUR} />
              <Pill value={row.quality} colours={QUALITY_COLOUR} />
              <Chip size="small" label={`spam ${Math.round((row.spam_probability || 0) * 100)}%`} />
              <Chip size="small" label={row.intent || '-'} />
              <Chip size="small" variant="outlined" label={`${row.engine} · ${row.model || ''}`} />
            </Stack>
            <Typography>{row.summary}</Typography>
            <Typography variant="body2" color="text.secondary">{result.action}</Typography>

            {Array.isArray(result.reasons) && result.reasons.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Why</Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {result.reasons.map((r, i) => <li key={i}><Typography variant="body2">{r}</Typography></li>)}
                </ul>
              </Box>
            )}

            {Array.isArray(row.flags) && row.flags.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {row.flags.map(f => <Chip key={f} size="small" label={f} color="warning" variant="outlined" />)}
              </Stack>
            )}

            {Array.isArray(result.signals) && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Signals — {result.raw_points} raw points of {result.reference_points} for a 100
                </Typography>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#1e293b', color: '#fff', textAlign: 'left' }}>
                      <th style={{ padding: '6px 8px' }}>Signal</th>
                      <th style={{ padding: '6px 8px' }}>Value</th>
                      <th style={{ padding: '6px 8px' }}>Threshold</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.signals.map(s => (
                      <tr key={s.signal} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 8px' }}>{s.signal}</td>
                        <td style={{ padding: '6px 8px' }}>{s.value === null || s.value === undefined ? '-' : String(s.value)}</td>
                        <td style={{ padding: '6px 8px', color: '#64748b' }}>{s.threshold}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: s.points ? '#6d28d9' : '#94a3b8' }}>
                          {s.points ? `+${s.points}` : '0'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Box>
            )}

            {Array.isArray(result.suggested_pitch) && result.suggested_pitch.length > 0 && (
              <Typography variant="body2"><b>Pitch:</b> {result.suggested_pitch.join(' · ')}</Typography>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Form as submitted</Typography>
              <pre style={{ margin: 0, padding: 12, background: '#f8fafc', borderRadius: 8, fontSize: 12, maxHeight: 320, overflow: 'auto' }}>
                {JSON.stringify(row.request, null, 2)}
              </pre>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ScoredLeads() {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [band, setBand] = useState('');
  const [quality, setQuality] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  const handleFetch = async () => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams({
      from: startDate.format('YYYY-MM-DD'),
      to: endDate.format('YYYY-MM-DD'),
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
      limit: '2000',
    });
    if (band) params.set('band', band);
    if (quality) params.set('quality', quality);
    try {
      const r = await fetch(`/api/lead-scores?${params}`, { cache: 'no-store' });
      const body = await r.json().catch(() => null);
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      setRows(body.items || []);
      setTotal(body.total ?? (body.items || []).length);
    } catch (err) {
      setRows([]);
      setTotal(null);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSelect = days => {
    setStartDate(dayjs().subtract(days, 'day'));
    setEndDate(dayjs());
  };

  const handleExportExcel = () => {
    const flat = rows.map(r => ({
      id: r.id,
      scored_at: dayjs(r.scored_at).format('YYYY-MM-DD HH:mm'),
      score: r.score,
      band: r.band,
      quality: r.quality,
      spam_probability: r.spam_probability,
      intent: r.intent,
      business_name: r.business_name,
      name: fullName(r),
      phone: r.phone,
      email: r.email,
      region: r.region,
      business_type: r.business_type,
      business_size: r.business_size,
      outlets: r.outlets,
      engine: r.engine,
      flags: (r.flags || []).join(', '),
      summary: r.summary,
    }));
    const ws = XLSX.utils.json_to_sheet(flat);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Scored leads');
    XLSX.writeFile(wb, `scored_leads_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'scored_at',
      header: 'Scored',
      size: 130,
      Cell: ({ cell }) => dayjs(cell.getValue()).format('DD/MM/YY HH:mm'),
    },
    {
      accessorKey: 'score',
      header: 'Score',
      size: 130,
      Cell: ({ cell, row }) => <ScoreCell value={cell.getValue()} band={row.original.band} />,
    },
    {
      accessorKey: 'band',
      header: 'Band',
      size: 90,
      filterVariant: 'select',
      filterSelectOptions: ['HOT', 'WARM', 'LOW'],
      Cell: ({ cell }) => <Pill value={cell.getValue()} colours={BAND_COLOUR} />,
    },
    {
      accessorKey: 'quality',
      header: 'Quality',
      size: 120,
      filterVariant: 'select',
      filterSelectOptions: ['GENUINE', 'SUSPICIOUS', 'SPAM'],
      Cell: ({ cell }) => <Pill value={cell.getValue()} colours={QUALITY_COLOUR} />,
    },
    { accessorKey: 'business_name', header: 'Business' },
    { id: 'name', header: 'Name', accessorFn: fullName },
    { accessorKey: 'phone', header: 'Phone', size: 140 },
    { accessorKey: 'email', header: 'Email' },
    { accessorKey: 'region', header: 'Region', size: 110 },
    { accessorKey: 'business_type', header: 'Type', size: 130 },
    { accessorKey: 'outlets', header: 'Outlets', size: 80 },
    { accessorKey: 'intent', header: 'Intent', size: 140 },
    {
      accessorKey: 'flags',
      header: 'Flags',
      Cell: ({ cell }) => (cell.getValue() || []).join(', ') || '-',
    },
    { accessorKey: 'engine', header: 'Engine', size: 100 },
    { accessorKey: 'summary', header: 'Summary', size: 320 },
  ], []);

  const hiddenCols = isMobile
    ? { email: false, region: false, business_type: false, outlets: false, intent: false, flags: false, engine: false, summary: false }
    : { engine: false };

  const counts = useMemo(() => {
    const c = { HOT: 0, WARM: 0, LOW: 0, SPAM: 0, SUSPICIOUS: 0 };
    rows.forEach(r => {
      if (c[r.band] !== undefined) c[r.band] += 1;
      if (r.quality === 'SPAM') c.SPAM += 1;
      if (r.quality === 'SUSPICIOUS') c.SUSPICIOUS += 1;
    });
    return c;
  }, [rows]);

  const selectSx = { width: isMobile ? '48%' : 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } };
  const dateSx = { width: isMobile ? '48%' : 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } };

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, background: '#fff' }}>
          <Stack spacing={isMobile ? 2 : 3}>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Scored Leads</h1>
              <p className="text-sm text-gray-500 mt-1">
                Every demo-form lead scored by <code>/api/lead-score</code> (Gemini) or <code>/api/v2/lead-score</code> (YC Engine).
                Click a row for the signals behind the score.
              </p>
            </div>

            {/* Filters */}
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={2}
              justifyContent="space-between"
              alignItems={isMobile ? 'stretch' : 'center'}
              sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 1 }}
            >
              <Stack direction="row" spacing={1} sx={{ width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }} useFlexGap>
                <DatePicker
                  label="From Date"
                  value={startDate}
                  onChange={v => v && setStartDate(v)}
                  maxDate={endDate}
                  slotProps={{ textField: { size: 'small', variant: 'outlined', sx: dateSx } }}
                />
                <DatePicker
                  label="To Date"
                  value={endDate}
                  onChange={v => v && setEndDate(v)}
                  minDate={startDate}
                  slotProps={{ textField: { size: 'small', variant: 'outlined', sx: dateSx } }}
                />
                <TextField select size="small" label="Band" value={band} onChange={e => setBand(e.target.value)} sx={selectSx}>
                  <MenuItem value="">All bands</MenuItem>
                  {['HOT', 'WARM', 'LOW'].map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Quality" value={quality} onChange={e => setQuality(e.target.value)} sx={selectSx}>
                  <MenuItem value="">All</MenuItem>
                  {['GENUINE', 'SUSPICIOUS', 'SPAM'].map(q => <MenuItem key={q} value={q}>{q}</MenuItem>)}
                </TextField>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
                  {[1, 7, 30].map(d => (
                    <Chip
                      key={d}
                      label={d === 1 ? 'Today' : `${d} Days`}
                      variant="outlined"
                      onClick={() => handleQuickSelect(d === 1 ? 0 : d)}
                      sx={{ height: 40, borderRadius: 1, borderColor: '#e2e8f0', '&:hover': { bgcolor: '#f1f5f9' } }}
                    />
                  ))}
                </Stack>
              </Stack>
              <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ width: isMobile ? '100%' : 'auto' }}>
                <Button
                  variant="contained"
                  onClick={handleFetch}
                  disabled={loading}
                  startIcon={<Refresh sx={{ fontSize: 20 }} />}
                  sx={{ backgroundColor: '#F96168', width: isMobile ? '100%' : 'auto' }}
                >
                  {loading ? 'Fetching…' : 'Fetch'}
                </Button>
                {rows.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={handleExportExcel}
                    startIcon={<Download sx={{ fontSize: 20 }} />}
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                  >
                    Excel
                  </Button>
                )}
              </Stack>
            </Stack>

            {error && <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm">{error}</div>}

            {total !== null && (
              <Grid container spacing={2}>
                {[
                  ['Scored', total, '#0f172a'],
                  ['Hot', counts.HOT, BAND_COLOUR.HOT],
                  ['Warm', counts.WARM, BAND_COLOUR.WARM],
                  ['Low', counts.LOW, BAND_COLOUR.LOW],
                  ['Suspicious', counts.SUSPICIOUS, QUALITY_COLOUR.SUSPICIOUS],
                  ['Spam', counts.SPAM, QUALITY_COLOUR.SPAM],
                ].map(([label, value, colour]) => (
                  <Grid item xs={6} sm={4} md={2} key={label}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                      <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#64748b' }}>{label}</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: colour }}>{value}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            {total !== null && (
              <Box sx={{ overflowX: 'auto' }}>
                <MaterialReactTable
                  columns={columns}
                  data={rows}
                  enableColumnFilters
                  enableGlobalFilter
                  enableSorting
                  enablePagination
                  state={{ isLoading: loading }}
                  initialState={{
                    columnVisibility: hiddenCols,
                    density: isMobile ? 'compact' : 'comfortable',
                    pagination: { pageSize: isMobile ? 10 : 25, pageIndex: 0 },
                    sorting: [{ id: 'scored_at', desc: true }],
                  }}
                  muiTableBodyRowProps={({ row }) => ({
                    onClick: () => setOpenId(row.original.id),
                    sx: { cursor: 'pointer', '&:hover': { bgcolor: '#fff7f7' } },
                  })}
                  muiTablePaperProps={{ sx: { borderRadius: 3, border: '1px solid #f1f5f9' } }}
                  muiTableHeadCellProps={{ sx: { bgcolor: '#fff', fontWeight: 700, color: '#0f172a', fontSize: '0.875rem' } }}
                  muiTableBodyCellProps={{ sx: { fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' } }}
                  renderEmptyRowsFallback={() => (
                    <Typography sx={{ p: 4, textAlign: 'center', color: '#64748b' }}>
                      No leads were scored between these dates.
                    </Typography>
                  )}
                />
              </Box>
            )}
          </Stack>
        </Container>
        <LeadDetail id={openId} onClose={() => setOpenId(null)} />
      </LocalizationProvider>
    </ThemeProvider>
  );
}
